import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Send, Phone, Mail, MapPin, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';

export const Contact = () => {
  const { t } = useTranslation();

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/393806331704', '_blank');
  };

  const handleLinkedInClick = () => {
    window.open('https://www.linkedin.com/in/victor-lazari/', '_blank');
  };

  return (
    <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto bg-white dark:bg-gray-900 min-h-screen">
      <SEO
        title={`${t('navigation.contact') || 'Contact'} – Victor Lazari`}
        description={t('contact.info.title') as string}
        canonical={"https://victorlazari.com/contact"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl md:text-5xl font-extrabold text-center mb-12 text-slate-900 dark:text-gray-100"
      >
        {t('contact.title')}
      </motion.h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
        >
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-gray-100 mb-6">{t('contact.info.title')}</h2>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Phone className="w-6 h-6 text-violet-700 dark:text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-gray-100">{t('contact.info.whatsapp.title')}</h3>
                <p className="text-slate-700 dark:text-gray-300">{t('contact.info.whatsapp.number')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Mail className="w-6 h-6 text-violet-700 dark:text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-gray-100">{t('contact.info.email.title')}</h3>
                <p className="text-slate-700 dark:text-gray-300">{t('contact.info.email.address')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-violet-700 dark:text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-gray-100">{t('contact.info.location.title')}</h3>
                <p className="text-slate-700 dark:text-gray-300">{t('contact.info.location.address')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 cursor-pointer" onClick={handleLinkedInClick}>
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Linkedin className="w-6 h-6 text-violet-700 dark:text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-gray-100">{t('contact.info.linkedin.title')}</h3>
                <p className="text-slate-700 dark:text-gray-300">{t('contact.info.linkedin.username')}</p>
              </div>
            </div>
          </div>

          <motion.button
            onClick={handleWhatsAppClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group relative w-full px-8 py-4 bg-violet-600 dark:bg-violet-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 overflow-hidden mt-8 hover:bg-violet-700 dark:hover:bg-violet-600"
          >
            <div className="absolute inset-0 bg-violet-700/10 transform translate-y-full transition-transform group-hover:translate-y-0" />
            <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            <span className="relative">{t('contact.whatsappButton')}</span>
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="relative rounded-lg overflow-hidden h-[400px]"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2852.5792927240777!2d12.096704576918982!3d44.3843947104402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477e0aa30d1d24ef%3A0xb6685f24df8eae8!2s48125%20San%20Pietro%20in%20Vincoli%20RA%2C%20Italy!5e0!3m2!1sen!2sus!4v1711054023457!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="rounded-lg"
            title={t('contact.map.title')}
            aria-label={t('contact.map.alt')}
          />
        </motion.div>
      </div>
    </div>
  );
};